import React, { useState, useRef } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { api, adminApi, fileUrl } from "@/lib/api";
import { Plus, Trash2, Upload, X, Save, User, Loader2 } from "lucide-react";

const ROLE_OPTIONS = ["chef", "hr", "forman", "other"];

const emptyMember = {
  role_key: "other",
  name: "",
  phone: "",
  email: "",
  image_url: null,
  sort_order: 99,
};

export default function AdminTeam({ adminKey }) {
  const { t } = useLanguage();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // member object being edited (or new)
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [initialized, setInitialized] = useState(false);
  const fileRef = useRef(null);

  const client = adminApi(adminKey);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/team");
      setMembers(res.data || []);
    } finally {
      setLoading(false);
    }
  };

  if (!initialized) {
    setInitialized(true);
    load();
  }

  const startNew = () => setEditing({ ...emptyMember });
  const startEdit = (m) => setEditing({ ...m });
  const cancel = () => {
    setEditing(null);
    setError("");
  };

  const update = (k) => (e) =>
    setEditing({ ...editing, [k]: e.target.value });

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await client.post("/uploads/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setEditing((prev) => ({ ...prev, image_url: res.data.url }));
    } catch (err) {
      setError(err?.response?.data?.detail || "Upload misslyckades");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    setError("");
    try {
      const payload = {
        role_key: editing.role_key,
        name: editing.name,
        phone: editing.phone,
        email: editing.email,
        image_url: editing.image_url || null,
        sort_order: Number(editing.sort_order) || 0,
      };
      if (editing.id) {
        await client.patch(`/team/${editing.id}`, payload);
      } else {
        await client.post("/team", payload);
      }
      setEditing(null);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Kunde inte spara");
    }
  };

  const remove = async (m) => {
    if (!window.confirm(t.admin.team.confirm_delete)) return;
    await client.delete(`/team/${m.id}`);
    await load();
  };

  return (
    <div data-testid="admin-team-panel">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <h2 className="font-display text-3xl md:text-4xl uppercase">
          {t.admin.team.title}
        </h2>
        <button
          onClick={startNew}
          data-testid="admin-team-add-btn"
          className="inline-flex items-center gap-2 bg-[#ff5a00] text-black px-5 py-3 text-xs tracking-[0.2em] uppercase font-bold hover:bg-[#ff7a30] transition-colors"
        >
          <Plus size={14} /> {t.admin.team.add}
        </button>
      </div>

      {loading ? (
        <div className="text-zinc-500 font-mono text-sm">{t.common.loading}</div>
      ) : members.length === 0 ? (
        <div className="border border-zinc-800 bg-[#0d0d10] p-12 text-center text-zinc-500">
          {t.admin.team.empty}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {members.map((m) => (
            <article
              key={m.id}
              data-testid={`admin-team-card-${m.id}`}
              className="border border-zinc-800 bg-[#0d0d10] overflow-hidden"
            >
              <div className="aspect-[4/3] bg-zinc-900 flex items-center justify-center overflow-hidden">
                {m.image_url ? (
                  <img
                    src={fileUrl(m.image_url)}
                    alt={m.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User size={48} className="text-zinc-700" />
                )}
              </div>
              <div className="p-4 border-t border-zinc-800">
                <div className="text-[10px] font-mono tracking-widest uppercase text-[#ff5a00]">
                  {t.team.roles[m.role_key] || m.role_key}
                </div>
                <div className="font-display text-xl uppercase mt-1">{m.name}</div>
                <div className="text-xs text-zinc-400 mt-2">{m.phone}</div>
                <div className="text-xs text-zinc-400 break-all">{m.email}</div>
                <div className="mt-4 flex gap-2">
                  <button
                    onClick={() => startEdit(m)}
                    data-testid={`admin-team-edit-${m.id}`}
                    className="flex-1 border border-zinc-700 text-white px-3 py-2 text-[11px] tracking-widest uppercase hover:border-[#ff5a00] hover:text-[#ff5a00] transition-colors"
                  >
                    Redigera
                  </button>
                  <button
                    onClick={() => remove(m)}
                    data-testid={`admin-team-delete-${m.id}`}
                    className="border border-zinc-700 text-zinc-500 px-3 py-2 hover:border-red-500 hover:text-red-400 transition-colors"
                    title={t.admin.delete}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {editing && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
          data-testid="admin-team-modal"
        >
          <div className="bg-[#0d0d10] border border-zinc-800 max-w-2xl w-full">
            <div className="flex items-center justify-between p-6 border-b border-zinc-800">
              <div className="font-display text-2xl uppercase">
                {editing.id ? "Redigera" : t.admin.team.add}
              </div>
              <button onClick={cancel} className="text-zinc-500 hover:text-white">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="text-xs font-mono tracking-[0.3em] uppercase text-zinc-500">
                  {t.admin.team.upload_image}
                </label>
                <div className="mt-3 flex gap-4 items-center">
                  <div className="w-24 h-24 bg-zinc-900 border border-zinc-800 flex items-center justify-center overflow-hidden shrink-0">
                    {editing.image_url ? (
                      <img
                        src={fileUrl(editing.image_url)}
                        alt="preview"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <User size={28} className="text-zinc-700" />
                    )}
                  </div>
                  <div className="flex-1">
                    <input
                      type="file"
                      ref={fileRef}
                      onChange={onUpload}
                      accept="image/png,image/jpeg,image/webp,image/gif"
                      className="hidden"
                      data-testid="admin-team-file-input"
                    />
                    <button
                      type="button"
                      onClick={() => fileRef.current?.click()}
                      disabled={uploading}
                      data-testid="admin-team-upload-btn"
                      className="inline-flex items-center gap-2 border border-zinc-700 text-white px-4 py-2 text-xs tracking-widest uppercase hover:border-[#ff5a00] hover:text-[#ff5a00] disabled:opacity-50"
                    >
                      {uploading ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <Upload size={14} />
                      )}
                      {uploading ? "Laddar upp..." : t.admin.team.upload_image}
                    </button>
                    {editing.image_url && (
                      <button
                        type="button"
                        onClick={() => setEditing({ ...editing, image_url: null })}
                        className="ml-2 text-xs text-zinc-500 hover:text-red-400"
                      >
                        Ta bort
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <Field
                label={t.admin.team.fields.role_key}
                isSelect
                value={editing.role_key}
                onChange={update("role_key")}
                options={ROLE_OPTIONS.map((r) => ({
                  value: r,
                  label: t.team.roles[r] || r,
                }))}
                testId="admin-team-role"
              />
              <Field
                label={t.admin.team.fields.sort_order}
                type="number"
                value={editing.sort_order}
                onChange={update("sort_order")}
                testId="admin-team-order"
              />
              <Field
                label={t.admin.team.fields.name}
                value={editing.name}
                onChange={update("name")}
                testId="admin-team-name"
              />
              <Field
                label={t.admin.team.fields.phone}
                value={editing.phone}
                onChange={update("phone")}
                testId="admin-team-phone"
              />
              <div className="md:col-span-2">
                <Field
                  label={t.admin.team.fields.email}
                  type="email"
                  value={editing.email}
                  onChange={update("email")}
                  testId="admin-team-email"
                />
              </div>
            </div>

            {error && (
              <div className="px-6 pb-2 text-sm text-red-400">{error}</div>
            )}

            <div className="p-6 border-t border-zinc-800 flex gap-3 justify-end">
              <button
                onClick={cancel}
                className="border border-zinc-700 text-white px-5 py-3 text-xs tracking-widest uppercase hover:border-zinc-500"
              >
                {t.admin.team.cancel}
              </button>
              <button
                onClick={save}
                data-testid="admin-team-save-btn"
                disabled={!editing.name || !editing.phone || !editing.email}
                className="inline-flex items-center gap-2 bg-[#ff5a00] text-black px-5 py-3 text-xs tracking-widest uppercase font-bold hover:bg-[#ff7a30] disabled:opacity-50"
              >
                <Save size={14} /> {t.admin.team.save}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, type = "text", value, onChange, testId, isSelect, options }) => (
  <div>
    <label className="text-xs font-mono tracking-[0.3em] uppercase text-zinc-500 block">
      {label}
    </label>
    {isSelect ? (
      <select
        data-testid={testId}
        className="brut-input"
        value={value}
        onChange={onChange}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value} className="bg-black">
            {o.label}
          </option>
        ))}
      </select>
    ) : (
      <input
        type={type}
        data-testid={testId}
        className="brut-input"
        value={value ?? ""}
        onChange={onChange}
      />
    )}
  </div>
);
